import { reactive, computed, watch, ref, nextTick } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import type {
  AdvancedScorecard_ReviewScorecard as ReviewScorecard,
  AdvancedScorecard_WorkspaceScorecard as WorkspaceScorecard,
} from '@zendesk/zqa-services/scorecards'
import { getWorkspaceScorecard, getWorkspaceScorecardsForReview } from '@/modules/shared/TicketContent/api'
import { session } from '@/composables/useSession'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import type { Category } from '@/modules/shared/Review/utils'
import { mapCategoriesById, getSortedGroupsFromCategories, getCategoriesFromScorecard } from './utils'

export interface State {
  // Scorecards for leaving new feedback
  scorecards: ReviewScorecard[]
  selectedScorecard?: WorkspaceScorecard
  categories: Category[]
  categoriesWithAnomalies: Category[]
}

export const defaultState: State = {
  scorecards: [],
  selectedScorecard: undefined,
  categories: [],
  categoriesWithAnomalies: [],
}

const SCORECARD_STORAGE_KEY = 'zqaSelectedScorecard.v2'

export default createSharedComposable(() => {
  const state = reactive(defaultState)
  let selectedScorecardId = ''

  const { connectionId, conversationId } = useTicketRouteParams()

  const generateScorecardStorageKey = (workspaceId: number) => `${workspaceId}.${SCORECARD_STORAGE_KEY}`

  const isDynamicScorecard = computed(() => false)

  const loadingScorecard = ref(false)

  async function setScorecard(selectedScorecard?: ReviewScorecard) {
    if (!selectedScorecard?.id) {
      state.selectedScorecard = undefined
      state.categories = []
      state.categoriesWithAnomalies = []
      return
    }

    selectedScorecardId = selectedScorecard.id

    loadingScorecard.value = true
    const sharedRequestParams = {
      scorecardId: selectedScorecard.id,
      externalTicketId: conversationId.value,
      connectionId: String(connectionId.value),
      conditional: true,
    }
    const [sc, scorecardWithAnomalies] = await Promise.all([
      getWorkspaceScorecard({
        ...sharedRequestParams,
        onlyActive: true,
        promptAnomalies: false,
      }),
      getWorkspaceScorecard({
        ...sharedRequestParams,
        onlyActive: false,
        promptAnomalies: true,
      }),
    ])

    if (selectedScorecardId === sc.id) {
      state.selectedScorecard = sc
      state.categories = getCategoriesFromScorecard(sc)
    }
    if (selectedScorecardId === scorecardWithAnomalies.id) {
      state.categoriesWithAnomalies = getCategoriesFromScorecard(scorecardWithAnomalies)
    }

    await nextTick()
    loadingScorecard.value = false
  }

  const _setDefaultScorecard = async (workspaceId: number) => {
    const key = generateScorecardStorageKey(workspaceId)
    const persisted: string | undefined = localStorage[key]
    const defaultScorecardId = persisted || state.selectedScorecard?.id

    if (defaultScorecardId) {
      const scorecard = state.scorecards.find((sc) => sc.id === defaultScorecardId)

      // Outdated scorecard persisted, remove it
      if (!scorecard) localStorage.removeItem(key)
      await setScorecard(scorecard || state.scorecards[0])
    } else {
      await setScorecard(state.scorecards[0])
    }
  }

  async function loadScorecards() {
    if (!session.features.advancedScorecardsDev) return
    if (!conversationId.value || !connectionId.value) return

    const { data: scorecards } = await getWorkspaceScorecardsForReview({
      externalTicketId: conversationId.value,
      connectionId: String(connectionId.value),
    })
    state.scorecards = scorecards

    if (scorecards[0] && !state.selectedScorecard) {
      await _setDefaultScorecard(session.workspace.id)
    }
  }

  const { data: scorecards } = useQuery({
    queryKey: ['advancedScorecards', conversationId, connectionId],
    queryFn: async () => {
      const { data } = await getWorkspaceScorecardsForReview({
        externalTicketId: conversationId.value,
        connectionId: String(connectionId.value),
      })
      return data
    },
    enabled: computed(() => !!(session.features.advancedScorecardsDev && conversationId.value && connectionId.value)),
  })

  watch(
    () => scorecards.value,
    async (scorecards) => {
      if (scorecards) {
        state.scorecards = scorecards

        if (!scorecards.length) return

        if (state.selectedScorecard) {
          await setScorecard(scorecards.find((s) => s.id === state.selectedScorecard.id))
        } else {
          await _setDefaultScorecard(session.workspace.id)
        }
      }
    },
  )

  const clearSelectedScorecard = async () => await setScorecard(undefined)

  const categoryOptions = computed(() => mapCategoriesById(state.categories))

  const getCategoryOptionsByScorecardId = async (scorecardId: string) => {
    if (!session.features.advancedScorecardsDev) return {}

    const scorecard = await getWorkspaceScorecard({
      scorecardId,
      externalTicketId: conversationId.value,
      connectionId: String(connectionId.value),
      conditional: false,
      onlyActive: false,
      promptAnomalies: false,
    })

    const categoriesByScorecard = getCategoriesFromScorecard(scorecard)
    return mapCategoriesById(categoriesByScorecard)
  }

  const activeScorecardCategories = computed(() => Object.keys(categoryOptions.value).map(Number))

  const categoryGroups = computed(() => getSortedGroupsFromCategories(categoryOptions.value))

  const getCategoryGroupsByScorecardId = async (scorecardId: string) => {
    const options = await getCategoryOptionsByScorecardId(scorecardId)
    return getSortedGroupsFromCategories(options)
  }

  const resetScorecards = () => {
    state.scorecards = []
  }

  const selectedScorecardName = computed(() => state.selectedScorecard?.name)

  async function setDraftedScorecard(scorecardId: string) {
    if (isDynamicScorecard.value) return
    if (!state.scorecards.length) await loadScorecards()

    const scorecard = state.scorecards?.find((s) => s.id === scorecardId)
    if (scorecard) await setScorecard(scorecard)
  }

  return {
    state,
    loadScorecards,
    setScorecard,
    loadingScorecard,
    clearSelectedScorecard,
    activeScorecardCategories,
    categoryOptions,
    getCategoryOptionsByScorecardId,
    categoryGroups,
    resetScorecards,
    isDynamicScorecard,
    selectedScorecardName,
    setDraftedScorecard,
    generateScorecardStorageKey,
    getCategoryGroupsByScorecardId,
  }
})
